/**
 * IHS-6 Season stats — Games Played, Victory %, Avg Possession %.
 * Layout per row: home value | label | away value.
 *
 * COMPACTION 2026-07-31 (Stat Row v2 / compact-by-default law): the
 * percentage rows dropped their 56px circular gauges for plain "62%"
 * values + the mirrored centre-out share bar (`renderType='bar'`) —
 * the market-standard treatment for two-sided share stats (Sofascore /
 * FotMob). Each row went ~64px → ~38px. Gauges remain reserved for
 * genuinely three-part visuals (composed 1X2 probability grids).
 *
 * Dot row above the rows acts as a legend so colours map cleanly to
 * teams without crowding the stat rows themselves.
 */

import type { ReactElement } from 'react';
import { useTranslation } from '../../../i18n/index.js';
import type { IhsSeasonStatsData } from '../../../types/ihs.js';
import { dashable } from '../../../utils/numbers.js';
import { IhsCard } from './IhsCard.js';
import { StatComparisonRow } from '../../primitives/StatComparisonRow.js';
import { LegendRow } from '../../primitives/LegendRow.js';
import { TEAM_HOME_COLOR, TEAM_AWAY_COLOR } from '../../../styles/team-colors.js';
import styles from './IhsSeasonStats.module.css';

const HOME_COLOR = TEAM_HOME_COLOR;
const AWAY_COLOR = TEAM_AWAY_COLOR;

/** 0..1 ratio → "62%" (or em-dash when the engine has no value). */
function pct(ratio: number | null): string {
  return ratio === null ? '—' : `${Math.round(ratio * 100)}%`;
}

/** Leader emphasis for a ratio pair — higher share leads; equal = 'both'.
 *  Undefined when either side is missing (both render plain). */
function pctLead(
  home: number | null,
  away: number | null,
): 'home' | 'away' | 'both' | undefined {
  if (home === null || away === null) return undefined;
  if (home === away) return 'both';
  return home > away ? 'home' : 'away';
}

export function IhsSeasonStats({
  data,
}: {
  data: IhsSeasonStatsData;
}): ReactElement {
  const { t } = useTranslation();
  const title =
    data.header?.title ??
    t('ihs.seasonStats.title', { defaultValue: 'Season stats' });
  const subtitle =
    data.header?.subtitle ?? `${data.competitionName} · ${data.seasonLabel}`;

  const winLead = pctLead(data.home.victoryRate, data.away.victoryRate);
  const possLead = pctLead(data.home.avgPossession, data.away.avgPossession);

  return (
    <IhsCard title={title} subtitle={subtitle}>
      <div className={styles.legendWrap}>
        <LegendRow
          items={[
            { label: data.home.team.name, color: HOME_COLOR },
            { label: data.away.team.name, color: AWAY_COLOR, position: 'right' },
          ]}
        />
      </div>

      {/* Plain count — numbers only (Stat Row v2: bars are explicit-ask,
          never value-parsed). */}
      <StatComparisonRow
        label={t('ihs.seasonStats.played', { defaultValue: 'Games Played' })}
        home={dashable(data.home.gamesPlayed)}
        away={dashable(data.away.gamesPlayed)}
        renderType="numbers"
        animateOnInView
      />

      <StatComparisonRow
        label={t('ihs.seasonStats.winRate', { defaultValue: '% Victory' })}
        home={pct(data.home.victoryRate)}
        away={pct(data.away.victoryRate)}
        renderType="bar"
        {...(winLead !== undefined ? { lead: winLead } : {})}
        animateOnInView
      />

      <StatComparisonRow
        label={t('ihs.seasonStats.possession', { defaultValue: '% Average Possession' })}
        home={pct(data.home.avgPossession)}
        away={pct(data.away.avgPossession)}
        renderType="bar"
        {...(possLead !== undefined ? { lead: possLead } : {})}
        animateOnInView
      />
    </IhsCard>
  );
}
